import { useState, useEffect, useCallback } from 'react';
import { Form, Input, Button, Card, Tabs, Typography, message, Spin } from 'antd';
import { SaveOutlined, FileProtectOutlined, SafetyOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { legalAPI } from '../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function LegalPage() {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [activeTab, setActiveTab] = useState('privacy');
    const [legalDocs, setLegalDocs] = useState({
        privacy: { type: 'privacy', title: 'Kebijakan Privasi', content: '' },
        terms: { type: 'terms', title: 'Syarat dan Ketentuan', content: '' },
        disclaimer: { type: 'disclaimer', title: 'Disclaimer', content: '' }
    });

    const fetchLegal = useCallback(async () => {
        try {
            setLoading(true);
            const data = await legalAPI.getAll();
            const docsMap = {
                privacy: { type: 'privacy', title: 'Kebijakan Privasi', content: '' },
                terms: { type: 'terms', title: 'Syarat dan Ketentuan', content: '' },
                disclaimer: { type: 'disclaimer', title: 'Disclaimer', content: '' }
            };

            if (Array.isArray(data)) {
                data.forEach(doc => {
                    if (docsMap[doc.type]) {
                        docsMap[doc.type] = doc;
                    }
                });
            }

            setLegalDocs(docsMap);
            form.setFieldsValue(docsMap[activeTab]);
        } catch (error) {
            console.error('Error fetching legal documents:', error);
            message.error('Failed to fetch legal documents');
        } finally {
            setLoading(false);
        }
    }, [activeTab, form]);

    useEffect(() => {
        fetchLegal();
    }, [fetchLegal]);

    useEffect(() => {
        // Update form when active tab changes
        form.setFieldsValue(legalDocs[activeTab]);
    }, [activeTab, legalDocs, form]);

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            const doc = { ...legalDocs[activeTab], ...values, type: activeTab };

            if (doc._id || doc.id) {
                await legalAPI.update(doc.id || doc._id, doc);
                message.success('Legal document updated successfully!');
            } else {
                await legalAPI.create(doc);
                message.success('Legal document created successfully!');
            }

            fetchLegal();
        } catch (error) {
            message.error('Error: ' + (error.response?.data?.message || error.message));
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" /></div>;
    }

    const tabItems = [
        {
            key: 'privacy',
            label: <span><FileProtectOutlined /> Privacy Policy</span>,
            children: null
        },
        {
            key: 'terms',
            label: <span><SafetyOutlined /> Terms & Conditions</span>,
            children: null
        },
        {
            key: 'disclaimer',
            label: <span><ExclamationCircleOutlined /> Disclaimer</span>,
            children: null
        }
    ];

    return (
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
            <div style={{ marginBottom: 24 }}>
                <Title level={2}><FileProtectOutlined /> Legal Documents</Title>
                <Text type="secondary">Manage privacy policy, terms & conditions, and disclaimer</Text>
            </div>

            <Card style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />

                <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: 24 }}>
                    <Form.Item name="title" label="Document Title" rules={[{ required: true }]}>
                        <Input size="large" placeholder="e.g., Kebijakan Privasi" />
                    </Form.Item>

                    <Form.Item name="content" label="Document Content" rules={[{ required: true }]}>
                        <TextArea
                            rows={20}
                            placeholder="Enter the full legal document content here...&#10;&#10;You can use markdown formatting if needed."
                        />
                    </Form.Item>

                    <Form.Item style={{ marginTop: 32 }}>
                        <Button
                            type="primary"
                            size="large"
                            htmlType="submit"
                            icon={<SaveOutlined />}
                            loading={submitting}
                            block
                            style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                border: 'none',
                                height: 48
                            }}
                        >
                            Save {activeTab === 'privacy' ? 'Privacy Policy' : activeTab === 'terms' ? 'Terms & Conditions' : 'Disclaimer'}
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
}
